/**
 * CVC Customer Notes Panel Component
 * Displays all notes for a customer product engagement in a rich card format
 * Uses data already joined in the main query - NO separate API call needed
 */

import { Icon, Link, mergeStyles, PanelType, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import React from "react";
import CustomPanel from "../../SharedComponents/CustomPanel/CustomPanel";
import { ICVCCustomerNotesPanelProps, ICVCCustomerInlineNote } from "./CVCCustomer.interfaces";

/**
 * Format date for display
 */
const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
};

// Color palette for note cards
const NOTE_COLORS = [
    { bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", light: "#f0f0ff" },
    { bg: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", light: "#e8f8f0" },
    { bg: "linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)", light: "#fff5eb" },
    { bg: "linear-gradient(135deg, #00b4db 0%, #0083b0 100%)", light: "#e8f7fc" },
    { bg: "linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)", light: "#f0f8f4" }
];

// Constants for content truncation
const COLLAPSED_LINE_HEIGHT = 1.7;
const COLLAPSED_MAX_LINES = 4;
const COLLAPSED_MAX_HEIGHT = COLLAPSED_LINE_HEIGHT * 14 * COLLAPSED_MAX_LINES;

/**
 * Single Note Card Component for inline notes (from joined query)
 */
const InlineNoteCard: React.FC<{ note: ICVCCustomerInlineNote; index: number }> = ({ note, index }) => {
    const colorScheme = NOTE_COLORS[index % NOTE_COLORS.length];
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [needsTruncation, setNeedsTruncation] = React.useState(false);
    const [isCopied, setIsCopied] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight;
            setNeedsTruncation(scrollHeight > COLLAPSED_MAX_HEIGHT + 10);
        }
    }, [note.notes]);

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const handleCopyNote = async () => {
        const contentToCopy = note.notes || "No content";
        try {
            await navigator.clipboard.writeText(contentToCopy);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch {
            const textArea = document.createElement("textarea");
            textArea.value = contentToCopy;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    return (
        <div style={{
            background: "#ffffff",
            borderRadius: 12,
            border: "1px solid #e1dfdd",
            marginBottom: 20,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
            transition: "box-shadow 0.2s ease"
        }}>
            {/* Header with gradient - shows Modified date and by */}
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                background: colorScheme.bg,
                color: "#ffffff"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                    <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        flexShrink: 0
                    }}>
                        {index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontSize: 14,
                            fontWeight: 600,
                            marginBottom: 2
                        }}>
                            {formatDateTime(note.modifiedOn1)}
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.9 }}>
                            by {note.modifiedBy1 || note.modifiedByHuman || "Unknown"}
                        </div>
                    </div>
                </div>

                {/* Copy Button */}
                <button
                    onClick={handleCopyNote}
                    title={isCopied ? "Copied!" : "Copy note content"}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 12px",
                        background: isCopied
                            ? "rgba(16, 124, 16, 0.9)"
                            : "rgba(255,255,255,0.2)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        outline: "none",
                        backdropFilter: "blur(4px)"
                    }}
                    onMouseEnter={(e) => {
                        if (!isCopied) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.35)";
                            e.currentTarget.style.transform = "scale(1.02)";
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isCopied) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                            e.currentTarget.style.transform = "scale(1)";
                        }
                    }}
                >
                    <Icon
                        iconName={isCopied ? "CheckMark" : "Copy"}
                        style={{ fontSize: 14 }}
                    />
                    {isCopied ? "Copied!" : "Copy"}
                </button>
            </div>

            {/* Subject section */}
            <div style={{
                padding: "12px 16px",
                background: colorScheme.light,
                borderBottom: "1px solid #e1dfdd"
            }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <Icon iconName="Tag" style={{ fontSize: 14, color: "#0078d4", marginTop: 2 }} />
                    <Text variant="medium" style={{ color: "#323130" }}>
                        <strong>Subject:</strong> {note.subject || "N/A"}
                    </Text>
                </div>
            </div>

            {/* Content with expand/collapse */}
            <div style={{ padding: 16, background: "#ffffff", position: "relative" }}>
                <div
                    ref={contentRef}
                    style={{
                        lineHeight: COLLAPSED_LINE_HEIGHT,
                        color: "#323130",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: 14,
                        maxHeight: isExpanded ? "none" : `${COLLAPSED_MAX_HEIGHT}px`,
                        overflow: "hidden",
                        transition: "max-height 0.3s ease-in-out"
                    }}
                >
                    {note.notes || "No content available"}
                </div>

                {/* View More / View Less Button */}
                {needsTruncation && (
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: isExpanded ? 12 : 8,
                        paddingTop: isExpanded ? 0 : 8
                    }}>
                        <button
                            onClick={toggleExpanded}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "8px 16px",
                                background: isExpanded
                                    ? "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)"
                                    : "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
                                color: isExpanded ? "#323130" : "#ffffff",
                                border: "none",
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: isExpanded
                                    ? "0 1px 3px rgba(0,0,0,0.1)"
                                    : "0 2px 6px rgba(0,120,212,0.3)",
                                transition: "all 0.2s ease",
                                outline: "none"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-1px)";
                                e.currentTarget.style.boxShadow = isExpanded
                                    ? "0 2px 6px rgba(0,0,0,0.15)"
                                    : "0 4px 10px rgba(0,120,212,0.4)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = isExpanded
                                    ? "0 1px 3px rgba(0,0,0,0.1)"
                                    : "0 2px 6px rgba(0,120,212,0.3)";
                            }}
                        >
                            <Icon
                                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                                style={{ fontSize: 12 }}
                            />
                            {isExpanded ? "View Less" : "View More"}
                        </button>
                    </div>
                )}
            </div>

            {/* Footer with creation and modification info */}
            <div style={{
                padding: "12px 16px",
                background: "#f8f8f8",
                borderTop: "1px solid #edebe9",
                display: "flex",
                flexDirection: "column",
                gap: 6
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon iconName="Add" style={{ fontSize: 12, color: "#107c10" }} />
                    <Text variant="small" style={{ color: "#323130" }}>
                        <strong>Created by:</strong> {note.createdBy1 || "Unknown"}
                    </Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon iconName="Edit" style={{ fontSize: 12, color: "#d83b01" }} />
                    <Text variant="small" style={{ color: "#323130" }}>
                        <strong>Modified:</strong> {formatDateTime(note.modifiedOn1)} by <strong>{note.modifiedBy1 || note.modifiedByHuman || "Unknown"}</strong>
                    </Text>
                </div>
            </div>
        </div>
    );
};

/**
 * CVC Customer Notes Panel - Main Component
 * Uses inline notes data from main query - NO separate API call needed
 */
const CVCCustomerNotesPanel: React.FC<ICVCCustomerNotesPanelProps> = ({
    isOpen,
    onDismiss,
    customerName,
    productName,
    customerProductURL,
    notesData,
    kustoLink,
    isLoading = false
}) => {
    const renderContent = () => {
        // Show loading spinner
        if (isLoading) {
            return (
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 300,
                    marginTop: 40
                }}>
                    <Spinner size={SpinnerSize.large} label="Loading notes..." />
                </div>
            );
        }

        const notes = notesData || [];

        if (notes.length === 0) {
            return (
                <div className={styles.emptyState} style={{ marginTop: 40 }}>
                    <Icon iconName="DocumentSearch" style={{ fontSize: 48, color: "#c8c6c4", marginBottom: 16 }} />
                    <Text variant="large" style={{ fontWeight: 600 }}>No Notes Found</Text>
                    <Text variant="medium" style={{ color: "#605e5c", marginTop: 8 }}>
                        No notes recorded for this engagement.
                    </Text>
                </div>
            );
        }

        return (
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 40 }}>
                {/* Customer & Solution Info */}
                <div style={{
                    padding: 16,
                    background: "#f8f8f8",
                    borderRadius: 8,
                    border: "1px solid #edebe9"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <Icon iconName="Contact" style={{ fontSize: 14, color: "#0078d4" }} />
                        <Text variant="medium" style={{ fontWeight: 600 }}>{customerName}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="ProductVariant" style={{ fontSize: 14, color: "#605e5c" }} />
                        <Text variant="medium" style={{ color: "#605e5c" }}>{productName}</Text>
                    </div>
                </div>

                {/* Stats bar with CRM and Kusto links */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #e8f4fd 0%, #f0f6ff 100%)",
                    borderRadius: 8,
                    border: "1px solid #d0e0f5"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon iconName="Documentation" style={{ fontSize: 18, color: "#0078d4" }} />
                        <Text variant="large" style={{ fontWeight: 700, color: "#0078d4" }}>{notes.length}</Text>
                        <Text variant="medium" style={{ color: "#605e5c" }}>Notes</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* CRM Link */}
                        {customerProductURL && customerProductURL.trim() && (
                            <Link
                                href={customerProductURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "6px 12px",
                                    background: "rgba(0,120,66,0.1)",
                                    borderRadius: 4,
                                    textDecoration: "none",
                                    fontSize: 13,
                                    color: "#107c10"
                                }}
                            >
                                <Icon iconName="Link" style={{ fontSize: 12 }} />
                                Open in CRM
                            </Link>
                        )}
                        {/* Kusto Link */}
                        {kustoLink && (
                            <Link
                                href={kustoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "6px 12px",
                                    background: "rgba(0,120,212,0.1)",
                                    borderRadius: 4,
                                    textDecoration: "none",
                                    fontSize: 13
                                }}
                            >
                                <Icon iconName="OpenInNewWindow" style={{ fontSize: 12 }} />
                                Explore Kusto
                            </Link>
                        )}
                    </div>
                </div>

                {/* Notes List - uses inline data from joined query */}
                <div>
                    {notes.map((note, index) => (
                        <InlineNoteCard key={`note-${index}`} note={note} index={index} />
                    ))}
                </div>
            </Stack>
        );
    };

    return (
        <CustomPanel
            dismissPanel={onDismiss}
            enablePanel={isOpen}
            isLoading={false}
            panelTitle="Notes History"
            panelType={PanelType.medium}
            customWidth="550px"
        >
            {renderContent()}
        </CustomPanel>
    );
};

// Styles
const styles = {
    emptyState: mergeStyles({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 24px",
        textAlign: "center"
    })
};

export default CVCCustomerNotesPanel;
